import { useState, useEffect, useCallback, useRef } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Plus,
  FileText,
  MoreHorizontal,
  Edit,
  Trash2,
  Save,
  X as CloseIcon,
} from "lucide-react"

import { customerNotesApi, type CustomerNote, type CreateCustomerNoteData } from "@crm-src/lib/customer-notes-api"
import { SearchInput } from "./search-input"

interface NotesPanelProps {
  customerId: string
  isLoading?: boolean
  title?: string
  className?: string
  currentUserName?: string
}



export function NotesPanel({
  customerId,
  isLoading: externalLoading = false,
  title = "Notes",
  className = "",
  currentUserName = "Current User",
}: NotesPanelProps) {
  const { toast} = useToast()
  const [notes, setNotes] = useState<CustomerNote[]>([])
  const [loading, setLoading] = useState(false)
  const [addingNote, setAddingNote] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState("")
  const [newNoteContent, setNewNoteContent] = useState("")
  const [noteToDelete, setNoteToDelete] = useState<CustomerNote | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  // Per-row actions menu anchor, keyed by note id.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; noteId: string } | null>(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 5
  const [totalNotes, setTotalNotes] = useState(0)

  // Search state
  const [searchQuery, setSearchQuery] = useState("")

  // Load notes when customerId, page, or search query changes
  useEffect(() => {
    if (customerId) {
      loadNotes()
    }
  }, [customerId, currentPage, searchQuery])

  // Search handlers passed to memoized SearchInput
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }, [])

  const handleClearSearch = useCallback(() => {
    setSearchQuery("")
    setCurrentPage(1)
  }, [])

  // Reset form when dialog closes
  useEffect(() => {
    if (!isDialogOpen) {
      setNewNoteContent("")
    }
  }, [isDialogOpen])

  // Track if this is the initial load (for showing loading spinner)
  const isInitialLoadRef = useRef(true)

  const loadNotes = async () => {
    try {
      // Only show loading spinner on initial load, not on search/pagination
      if (isInitialLoadRef.current) {
        setLoading(true)
      }
      const response = await customerNotesApi.getAll(customerId, {
        page: currentPage,
        per_page: itemsPerPage,
        search: searchQuery || undefined,
        sort: 'note_date',
        direction: 'desc'
      })
      const fetchedNotes = response.notes
      const totalCount = response.pagination?.total_count || 0

      setNotes(fetchedNotes)
      setTotalNotes(totalCount)
      isInitialLoadRef.current = false

      // If current page is empty but there are items on previous pages, go back
      if (fetchedNotes.length === 0 && totalCount > 0 && currentPage > 1) {
        setCurrentPage(currentPage - 1)
      }
    } catch (error: any) {
      // Ignore cancelled requests (common in React Strict Mode)
      const isCancelled = error.code === 'ERR_NETWORK_CHANGED' ||
                         error.code === 'ERR_CANCELED' ||
                         (error.message === 'Network Error' && !error.response && !error.request?.status);

      if (isCancelled) {
        // Silently ignore cancelled requests
        setLoading(false)
        return
      }

      console.error('Error loading notes:', error)
      toast({
        title: "Error",
        description: "Failed to load notes",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return

    try {
      setAddingNote(true)
      const response = await customerNotesApi.create(customerId, {
        content: newNoteContent.trim()
      })

      setNotes(prev => [response.note, ...prev])
      setIsDialogOpen(false)
      toast({
        title: "Success",
        description: "Note added successfully",
      })
    } catch (error: any) {
      console.error('Error adding note:', error)
      const errorMessage = error.response?.data?.errors?.[0] || error.response?.data?.error || "Failed to add note"
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setAddingNote(false)
    }
  }

  const handleEditNote = async (noteId: string) => {
    if (!editContent.trim()) return

    try {
      const response = await customerNotesApi.update(customerId, noteId, {
        content: editContent.trim(),
      })

      setNotes(prev => prev.map(note =>
        note.id === noteId ? response.note : note
      ))
      setEditingNoteId(null)
      setEditContent("")
      toast({
        title: "Success",
        description: "Note updated successfully",
      })
    } catch (error) {
      console.error('Error editing note:', error)
      toast({
        title: "Error",
        description: "Failed to update note",
        variant: "destructive",
      })
    }
  }

  const startEditing = (note: CustomerNote) => {
    setEditingNoteId(note.id)
    setEditContent(note.content)
  }

  const cancelEditing = () => {
    setEditingNoteId(null)
    setEditContent("")
  }



  const handleDeleteNote = async (noteId: string) => {
    try {
      await customerNotesApi.delete(customerId, noteId)

      // Check if this was the last item on the current page
      // If so and we're not on page 1, go to previous page
      const remainingNotesOnPage = notes.filter(note => note.id !== noteId).length
      if (remainingNotesOnPage === 0 && currentPage > 1) {
        // Go to previous page - this will trigger loadNotes via useEffect
        setCurrentPage(prev => prev - 1)
      } else {
        // Reload current page to get fresh data from server
        loadNotes()
      }

      toast({
        title: "Success",
        description: "Note deleted successfully",
      })
    } catch (error) {
      console.error('Error deleting note:', error)
      toast({
        title: "Error",
        description: "Failed to delete note",
        variant: "destructive",
      })
    }
  }

  const openDeleteConfirmation = (note: CustomerNote) => {
    setNoteToDelete(note)
    setIsDeleteDialogOpen(true)
  }

  const closeDeleteConfirmation = () => {
    setNoteToDelete(null)
    setIsDeleteDialogOpen(false)
  }

  const confirmDeleteNote = async () => {
    if (noteToDelete) {
      await handleDeleteNote(noteToDelete.id)
      closeDeleteConfirmation()
    }
  }

  const handleAddNoteKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (newNoteContent.trim() && !addingNote) {
        handleAddNote()
      }
    }
  }

  const handleEditNoteKeyDown = (e: React.KeyboardEvent, noteId: string) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (editContent.trim()) {
        handleEditNote(noteId)
      }
    }
  }



  if (loading || externalLoading) {
    return (
      <Card {...(className ? { className } : {})}>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FileText size={20} />
              <Typography component="span" sx={{ fontWeight: 600 }}>{title}</Typography>
            </Box>
          }
        />
        <CardContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={48} />
            ))}
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card {...(className ? { className } : {})}>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FileText size={20} />
              <Typography component="span" sx={{ fontWeight: 600 }}>{title}</Typography>
            </Box>
            <Button size="small" variant="outlined" color="inherit" startIcon={<Plus size={16} />} onClick={() => setIsDialogOpen(true)}>
              Add
            </Button>
          </Box>
        }
      />
      <CardContent>
        {/* Memoized Search Input - isolated from parent re-renders */}
        <SearchInput
          placeholder="Search notes..."
          onSearch={handleSearch}
          onClear={handleClearSearch}
          searchQuery={searchQuery}
        />

        {loading ? (
          <Box sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
            <Box sx={{ display: "flex", justifyContent: "center", color: "text.disabled", mb: 1 }}><FileText size={32} /></Box>
            <Typography sx={{ fontSize: 14 }}>Loading...</Typography>
          </Box>
        ) : notes.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
            <Box sx={{ display: "flex", justifyContent: "center", color: "text.disabled", mb: 1 }}><FileText size={32} /></Box>
            <Typography sx={{ fontSize: 14 }}>{searchQuery ? `No notes found matching "${searchQuery}"` : 'No notes added yet'}</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {notes.map((note) => (
              <Box key={note.id} sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1, "&:hover": { bgcolor: "action.hover" } }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.25 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 12, color: "text.secondary" }}>
                    <Box component="span">{note.author?.name || 'Unknown User'}</Box>
                    <Box component="span">•</Box>
                    <Box component="span">{new Date(note.note_date).toLocaleString()}</Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton size="small" sx={{ width: 32, height: 32 }} aria-label="Open menu" onClick={(e) => setMenuAnchor({ el: e.currentTarget, noteId: note.id })}>
                      <MoreHorizontal size={16} />
                    </IconButton>
                    <Menu
                      anchorEl={menuAnchor?.noteId === note.id ? menuAnchor.el : null}
                      open={menuAnchor?.noteId === note.id}
                      onClose={() => setMenuAnchor(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <MenuItem onClick={() => { setMenuAnchor(null); startEditing(note) }}>
                        <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
                        Edit
                      </MenuItem>
                      <MenuItem onClick={() => { setMenuAnchor(null); openDeleteConfirmation(note) }} sx={{ color: "error.main" }}>
                        <Box sx={{ mr: 1, display: "flex" }}><Trash2 size={16} /></Box>
                        Delete
                      </MenuItem>
                    </Menu>
                  </Box>
                </Box>

                {editingNoteId === note.id ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <TextField
                      multiline
                      minRows={3}
                      fullWidth
                      size="small"
                      placeholder="Press Enter to save, Shift+Enter for new line"
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      onKeyDown={(e) => handleEditNoteKeyDown(e, note.id)}
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleEditNote(note.id)}
                        disabled={!editContent.trim()}
                        startIcon={<Save size={16} />}
                      >
                        Save
                      </Button>
                      <Button size="small" variant="outlined" color="inherit" onClick={cancelEditing} startIcon={<CloseIcon size={16} />}>
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{note.content}</Typography>
                )}
              </Box>
            ))}
          </Box>

          {/* Pagination Controls */}
          {totalNotes > itemsPerPage && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                  {currentPage} / {Math.ceil(totalNotes / itemsPerPage)}
                </Typography>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalNotes / itemsPerPage), prev + 1))}
                  disabled={currentPage >= Math.ceil(totalNotes / itemsPerPage)}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </>
        )}
      </CardContent>

      {/* Add Note Dialog */}
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Note</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Add a note to keep track of important information and interactions.
          </DialogContentText>
          <TextField
            multiline
            minRows={4}
            fullWidth
            size="small"
            placeholder="Enter your note here... (Press Enter to submit, Shift+Enter for new line)"
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            onKeyDown={handleAddNoteKeyDown}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddNote}
            disabled={!newNoteContent.trim() || addingNote}
          >
            {addingNote ? "Adding..." : "Add Note"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog. `customer_notes#destroy` calls
          `CustomerNote#soft_delete!` — `deleted_at` + `active: false`, under a
          `not_deleted` default_scope. Nothing is erased.
          See docs/crm.md → "Delete is a soft delete". */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Note</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to delete this note? It is removed from the note list.
            The note itself is kept, so an administrator can restore it.
          </DialogContentText>
          {noteToDelete && (
            <Box sx={{ p: 1.5, bgcolor: "surfaceSoft", border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 12, color: "text.secondary", mb: 0.25 }}>
                <Box component="span">{noteToDelete.author?.name || 'Unknown User'}</Box>
                <Box component="span">•</Box>
                <Box component="span">{new Date(noteToDelete.note_date).toLocaleString()}</Box>
              </Box>
              <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{noteToDelete.content}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={closeDeleteConfirmation}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteNote}
          >
            Delete Note
          </Button>
        </DialogActions>
      </Dialog>

    </Card>
  )
}
